import {
	CheckboxGroup,
	CheckboxGroupItem,
	RadioButtonGroup,
	RadioButtonGroupItem,
	ValidationError
} from "client/Components";
import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import { IValueInputAttachable } from "client/Forms/attachable";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import { hasMinimumEntries } from "client/Validation/rules";
import { ReactElement } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { Device } from "client/Api/common";

export type DevicesValue = {
	restrictDevices: boolean;
	devices: readonly Device[];
};

export type DevicesProps = {
	attachable: IValueInputAttachable<DevicesValue>;
};

export const Devices = (props: DevicesProps): ReactElement => {
	const {
		attachable
	} = props;

	return (
		<div>
			<NoUniqueInputIdContext>
				<RadioButtonGroup
					{...attachable.for("restrictDevices").attachToRadioButtonGroup()}
					options={restrictDeviceOptions}
				/>

				{attachable.for("restrictDevices").value && (
					<>
						<CheckboxGroup
							{...attachable.for("devices").attachToCheckboxGroup()}
							options={deviceOptions}
						/>
						<ValidationError {...attachable.for("devices").attachToValidationError()} />
					</>
				)}
			</NoUniqueInputIdContext>
		</div>
	);
};
Devices.displayName = "Devices";

const restrictDeviceOptions: readonly RadioButtonGroupItem<boolean>[] = [
	{
		text: localize("_AllDevices"),
		value: false
	},
	{
		text: localize("_SelectDevices"),
		value: true
	}
];

const deviceOptions: readonly CheckboxGroupItem<Device>[] = [
	{
		text: localize("_Desktop") + " " + localize("_IncludingNotebook"),
		value: Device.Desktop
	},
	{
		text: localize("_Tablet") + " " + localize("_IncludingChromebook"),
		value: Device.Tablet
	},
	{
		text: localize("_Mobile"),
		value: Device.Mobile
	}
];

export function createDevicesValidation<TModel extends DevicesValue>(): IValidationConfiguration<TModel> {
	const result: IValidationConfiguration<TModel> = createValidationConfiguration<TModel>();

	const conditionalValidator: IValidationConfiguration<TModel> = result.when(x => x.restrictDevices);

	const devicesValidator = conditionalValidator.forCollection("devices");

	devicesValidator.add(
		hasMinimumEntries(
			1,
			localize("_AtLeastOneDevice")
		)
	);

	return result;
}